import {
  BaseApiService,
  type JsonApiResponse,
  type ServiceOptions,
} from './base.service';
import type { Control, ControlStoreResponse } from '@/types/concept';
import {
  imageUploadService,
  type ProcessedFile,
} from '@/shared/lib/services/image-upload.service';
import {
  setMutationLock,
  clearMutationLock,
} from '@/shared/api/websocket/realtime-sync.locks';

export class ControlsService extends BaseApiService {
  async getControlsForAngle(
    angleId: string,
    options?: ServiceOptions
  ): Promise<Control[]> {
    const response = await this.httpGet<JsonApiResponse<Control[]>>(
      `/v1/controls/angles/${angleId}`,
      undefined,
      {
        loading: { trackEndpoint: `controls-angle-${angleId}` },
        ...options,
      }
    );
    return response.data;
  }

  async getControl(
    controlId: string,
    options?: ServiceOptions
  ): Promise<Control> {
    const response = await this.httpGet<JsonApiResponse<Control>>(
      `/v1/controls/${controlId}`,
      undefined,
      {
        loading: { trackEndpoint: `controls-get-${controlId}` },
        ...options,
      }
    );
    return response.data;
  }

  async deleteControl(
    controlId: string,
    forceDelete: boolean = false,
    options?: ServiceOptions
  ): Promise<void> {
    const queryString = this.buildQueryString({
      force_delete: forceDelete || undefined,
    });
    const url = `/v1/controls/${controlId}${queryString ? `?${queryString}` : ''}`;

    await this.httpDelete(url, undefined, {
      loading: { trackEndpoint: `controls-delete-${controlId}` },
      ...options,
    });
  }

  /**
   * Confirm control upload completed
   */
  async confirmUpload(
    controlId: string,
    options?: ServiceOptions
  ): Promise<ControlStoreResponse> {
    const response = await this.post<JsonApiResponse<ControlStoreResponse>>(
      `/v1/controls/${controlId}/confirm`,
      undefined,
      undefined,
      {
        loading: { trackEndpoint: `controls-confirm-${controlId}` },
        ...options,
      }
    );
    return response.data;
  }

  async uploadControl(
    angleId: string,
    controlType: Control['attributes']['control_type'],
    file: File | Blob,
    options?: ServiceOptions
  ): Promise<Control> {
    // Lock the local controls entity for the duration of the upload to keep this
    // tab from refetching mid-mutation (the lock pairs with WebSocket auto-locks
    // for cross-device coherence). The CDN 404 race itself is now handled
    // server-side by the Uploadable contract — the API withholds the file URL
    // until confirmed_at is set, so other devices won't fetch a pre-upload URL.
    setMutationLock('controls', 30_000);
    try {
      // Controls are saved as PNG with transparency (user edits only)
      const fileToUpload =
        file instanceof File
          ? file
          : new File([file], 'control.png', { type: 'image/png' });

      const result = await imageUploadService.uploadSingle(
        fileToUpload,
        {
          type: 'control',
          onCleanupFailure: async (id: string) => {
            await this.deleteControl(id);
          },
        },
        async (processedFile: ProcessedFile) => {
          const requestData = this.formatJsonApiData('control', {
            angle_id: angleId,
            file_type: controlType,
            md5: processedFile.md5,
            file_name: processedFile.file.name,
            content_type: processedFile.file.type,
            // Final (post-resize) pixel dimensions of the control image.
            width: processedFile.width,
            height: processedFile.height,
          });

          const storeResponse = await this.post<
            JsonApiResponse<ControlStoreResponse>
          >('/v1/controls', requestData, undefined, {
            loading: { trackEndpoint: `controls-upload-${angleId}` },
            ...options,
          });

          const control = storeResponse.data;
          return {
            id: control.id,
            uploadPolicy: control.attributes.upload_policy,
          };
        },
        (id) => this.confirmUpload(id, options)
      );

      const completeControl = await this.getControl(result.id, options);
      clearMutationLock('controls');
      return completeControl;
    } catch (error) {
      clearMutationLock('controls');
      throw error;
    }
  }

  async updateControl(
    controlId: string,
    controlType: Control['attributes']['control_type'],
    file: File | Blob,
    options?: ServiceOptions
  ): Promise<{ control: Control; action: 'updated' | 'created' }> {
    // Lock the local controls entity for the duration of the update to keep this
    // tab from refetching mid-mutation. CDN 404 race is handled server-side now
    // (Uploadable contract), so this lock is only about local mutation coherence.
    setMutationLock('controls', 30_000);
    try {
      // Controls are saved as PNG with transparency (user edits only)
      const fileToUpload =
        file instanceof File
          ? file
          : new File([file], 'control.png', { type: 'image/png' });

      // Variable to capture action from the upload callback
      let capturedAction: 'updated' | 'created' = 'updated';

      const result = await imageUploadService.uploadSingle(
        fileToUpload,
        {
          type: 'control',
          onCleanupFailure: async (id: string) => {
            // If cleanup fails, try to delete the control
            // (will only work if it wasn't versioned)
            try {
              await this.deleteControl(id);
            } catch {
              // Ignore cleanup errors
            }
          },
        },
        async (processedFile: ProcessedFile) => {
          const requestData = this.formatJsonApiData('control', {
            file_type: controlType,
            md5: processedFile.md5,
            file_name: processedFile.file.name,
            content_type: processedFile.file.type,
            // Final (post-resize) pixel dimensions of the control image.
            width: processedFile.width,
            height: processedFile.height,
          });

          const updateResponse = await this.patch<
            JsonApiResponse<ControlStoreResponse> & {
              meta: { action: 'updated' | 'created' };
            }
          >(`/v1/controls/${controlId}`, requestData, undefined, {
            loading: { trackEndpoint: `controls-update-${controlId}` },
            ...options,
          });

          const control = updateResponse.data;
          capturedAction = updateResponse.meta.action;

          return {
            id: control.id,
            uploadPolicy: control.attributes.upload_policy,
          };
        },
        (id) => this.confirmUpload(id, options)
      );

      const completeControl = await this.getControl(result.id, options);
      clearMutationLock('controls');
      return {
        control: completeControl,
        action: capturedAction,
      };
    } catch (error) {
      clearMutationLock('controls');
      throw error;
    }
  }
}

export const controlsService = new ControlsService();
